import React, { useCallback, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Paperclip, 
  Image, 
  FileText, 
  Music, 
  Video, 
  Archive, 
  X, 
  Upload,
  Loader2
} from 'lucide-react';
import { cn } from '@flowstate/utils';
import { toast } from '@/components/ui/sonner';
import { chatAttachmentService } from '../services/chatAttachmentService';
import { MessageAttachment } from '@flowstate/types';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface FileUploadProps {
  conversationId: string;
  onFileUploaded?: (attachment: MessageAttachment) => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
  disabled?: boolean;
  buttonClassName?: string;
  className?: string;
}

interface UploadingFile {
  id: string;
  file: File;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  conversationId,
  onFileUploaded,
  onUploadStart,
  onUploadEnd,
  disabled = false,
  buttonClassName,
  className
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const getFileIcon = (fileType: string) => {
    const category = chatAttachmentService.getFileCategory(fileType);
    switch (category) {
      case 'image': return <Image className="h-4 w-4" />;
      case 'document': return <FileText className="h-4 w-4" />;
      case 'audio': return <Music className="h-4 w-4" />;
      case 'video': return <Video className="h-4 w-4" />;
      case 'archive': return <Archive className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) {return '0 Bytes';}
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileUpload = useCallback(async (files: FileList | File[]) => {
    if (disabled) {return;}

    const fileArray = Array.from(files);
    if (fileArray.length === 0) {return;}

    onUploadStart?.();

    const newUploadingFiles: UploadingFile[] = fileArray.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      progress: 0,
      status: 'uploading'
    }));

    setUploadingFiles(prev => [...prev, ...newUploadingFiles]);

    for (const uploadingFile of newUploadingFiles) {
      try {
        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setUploadingFiles(prev => prev.map(f => 
            f.id === uploadingFile.id 
              ? { ...f, progress: Math.min(f.progress + 10, 90) }
              : f
          ));
        }, 100);

        const response = await chatAttachmentService.uploadChatAttachment({
          file: uploadingFile.file,
          conversation_id: conversationId,
          display_name: uploadingFile.file.name
        });

        clearInterval(progressInterval);

        if (response.error) {
          const errorMessage = response.error || 'Unknown error';
          setUploadingFiles(prev => prev.map(f => 
            f.id === uploadingFile.id 
              ? { ...f, status: 'error', error: errorMessage, progress: 100 }
              : f
          ));
          toast.error(`Failed to upload ${uploadingFile.file.name}: ${errorMessage}`);
        } else if (response.data) {
          setUploadingFiles(prev => prev.map(f => 
            f.id === uploadingFile.id 
              ? { ...f, status: 'success', progress: 100 }
              : f
          ));
          // Transform ChatAttachment to MessageAttachment format
          const messageAttachment = {
            id: response.data.id,
            url: response.data.public_url,
            type: response.data.file_type,
            name: response.data.display_name || response.data.file_name,
            size: response.data.file_size
          };
          onFileUploaded?.(messageAttachment);
          toast.success(`Successfully uploaded ${uploadingFile.file.name}`);
        }
      } catch (_error) {
        setUploadingFiles(prev => prev.map(f => 
          f.id === uploadingFile.id 
            ? { ...f, status: 'error', error: 'Upload failed', progress: 100 }
            : f
        ));
        toast.error(`Failed to upload ${uploadingFile.file.name}`);
      }
    }

    // Clean up completed uploads after a delay
    setTimeout(() => {
      setUploadingFiles(prev => prev.filter(f => f.status === 'uploading'));
    }, 3000);

    onUploadEnd?.();
  }, [conversationId, disabled, onFileUploaded, onUploadStart, onUploadEnd]);

  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      handleFileUpload(files);
    }
    // Reset input value to allow uploading the same file again
    event.target.value = '';
  }, [handleFileUpload]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files) {
      handleFileUpload(files);
    }
  }, [handleFileUpload]);

  const handlePaste = useCallback(async (event: ClipboardEvent) => {
    const file = await chatAttachmentService.handleClipboardPaste(event);
    if (file) {
      handleFileUpload([file]);
    }
  }, [handleFileUpload]);

  // Add paste event listener
  React.useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  const triggerFileInput = (accept?: string) => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = accept || '*/*';
      fileInputRef.current.click();
    }
  };

  const removeUploadingFile = (id: string) => {
    setUploadingFiles(prev => prev.filter(f => f.id !== id));
  };

  return (
    <div className={cn("relative", className)}>
      {/* Hidden file input */}
      <Input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Upload button with dropdown */}
      <TooltipProvider>
        <Tooltip>
          <DropdownMenu>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={disabled}
                  className={cn(
                    "relative hover:bg-gray-200 dark:hover:bg-gray-700",
                    buttonClassName,
                    isDragOver && "bg-primary/10 border-primary border-2 border-dashed"
                  )}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>Upload files or paste images</p>
            </TooltipContent>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem onClick={() => triggerFileInput('image/*')}>
                <Image className="mr-2 h-4 w-4" />
                Upload Images
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => triggerFileInput('.pdf,.doc,.docx,.txt,.md')}>
                <FileText className="mr-2 h-4 w-4" />
                Upload Documents
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => triggerFileInput('audio/*')}>
                <Music className="mr-2 h-4 w-4" />
                Upload Audio
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => triggerFileInput('video/*')}>
                <Video className="mr-2 h-4 w-4" />
                Upload Video
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => triggerFileInput()}>
                <Upload className="mr-2 h-4 w-4" />
                Upload Any File
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      </TooltipProvider>

      {/* Upload progress indicators */}
      {uploadingFiles.length > 0 && (
        <div className="absolute bottom-full mb-2 left-0 right-0 space-y-1">
          {uploadingFiles.map((uploadingFile) => (
            <div
              key={uploadingFile.id}
              className="bg-background border rounded-lg p-2 shadow-lg flex items-center gap-2 text-sm"
            >
              {getFileIcon(uploadingFile.file.type)}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="truncate font-medium">
                    {uploadingFile.file.name}
                  </span>
                  <span className="text-xs text-muted-foreground ml-2">
                    {formatFileSize(uploadingFile.file.size)}
                  </span>
                </div>
                
                {uploadingFile.status === 'uploading' && (
                  <div className="flex items-center gap-2 mt-1">
                    <div 
                      className="flex-1 bg-muted rounded-full h-1 relative overflow-hidden"
                      style={{ '--upload-progress': `${uploadingFile.progress}%` } as React.CSSProperties}
                    >
                      <div className="bg-primary h-1 rounded-full transition-all duration-300 absolute top-0 left-0 upload-progress-fill" />
                    </div>
                    <Loader2 className="h-3 w-3 animate-spin" />
                  </div>
                )}
                
                {uploadingFile.status === 'error' && (
                  <div className="text-xs text-destructive mt-1">
                    {uploadingFile.error}
                  </div>
                )}
                
                {uploadingFile.status === 'success' && (
                  <div className="text-xs text-green-600 mt-1">
                    Upload complete
                  </div>
                )}
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0"
                onClick={() => removeUploadingFile(uploadingFile.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Drag and drop overlay */}
      {isDragOver && (
        <div className="fixed inset-0 bg-primary/10 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-background border-2 border-dashed border-primary rounded-lg p-8 text-center">
            <Upload className="h-12 w-12 mx-auto mb-4 text-primary" />
            <p className="text-lg font-medium">Drop files here to upload</p>
            <p className="text-sm text-muted-foreground">
              Images, documents, audio, video, and more
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
